import { useEffect, useRef } from 'react';

/**
 * Provides a boolean indicating whether the page was hard-reloaded and calls
 * `onReloadDetected` exactly once on that first render so the consumer can
 * perform cleanup (e.g., clear URL params).
 * 
 * Modified to be less aggressive and only clear state when necessary.
 */
export const useSoftReload = (onReloadDetected?: () => void): boolean => {
  // Persisted value, evaluated once during initial render.
  const isReloadingRef = useRef<boolean>(
    typeof window !== 'undefined' && sessionStorage.getItem('isReloading') === 'true'
  );

  // Set flag before the page unloads so we can detect reloads.
  useEffect(() => {
    const handler = () => {
      // Only set the reload flag if we're not already on a clean state
      const currentUrl = window.location.href;
      const hasUrlParams = currentUrl.includes('?') || currentUrl.includes('#');
      
      // Only mark as reloading if we have URL parameters that might need clearing
      if (hasUrlParams) {
        sessionStorage.setItem('isReloading', 'true');
      }
    };
    
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, []);

  // Clear the flag and invoke callback once after reload is detected.
  useEffect(() => {
    if (isReloadingRef.current) {
      sessionStorage.removeItem('isReloading');
      onReloadDetected?.();
      // Prevent subsequent re-renders from thinking it's still a reload.
      isReloadingRef.current = false;
    }
  }, [onReloadDetected]);

  return isReloadingRef.current;
}; 